// src/pages/SettingsOrganization.jsx

import React, { useState, useEffect } from 'react';
import { Building2, Users, BarChart3, Zap, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { apiCall } from '../utils/api';

export default function SettingsOrganization() {
  const [org, setOrg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [orgRes, usageRes] = await Promise.all([
          apiCall('/organization/'),
          apiCall('/organization/usage'),
        ]);
        if (orgRes.ok) setOrg(await orgRes.json());
        if (usageRes.ok) setUsage(await usageRes.json());
      } catch (err) {
        console.error('Failed to load organization:', err);
        setError('Could not load organization data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
        <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-white">Organization</h2>
        <p className="text-sm text-[#a0a0a0] mt-1">
          Your organization details and plan usage
        </p>
      </div>

      {/* Organization info */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-white">
                {org?.name || 'Organization'}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant="secondary">Plan: {org?.plan}</Badge>
                <Badge
                  variant="default"
                  className="bg-green-500/20 text-green-400 border-green-500/30"
                >
                  {org?.status || 'active'}
                </Badge>
                {org?.trial_ends_at && (
                  <Badge variant="outline">
                    Trial ends {new Date(org.trial_ends_at).toLocaleDateString()}
                  </Badge>
                )}
              </div>
              {org?.created_at && (
                <p className="text-xs text-[#a0a0a0] mt-3">
                  Created {new Date(org.created_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage metrics */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3">Usage</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <UsageCard
            icon={Users}
            label="Team members"
            current={usage?.users?.current}
            maximum={usage?.users?.maximum}
          />
          <UsageCard
            icon={BarChart3}
            label="Charts"
            current={usage?.charts?.current}
            maximum={usage?.charts?.maximum}
          />
          <UsageCard
            icon={Zap}
            label="Connectors"
            current={usage?.connectors?.current}
            maximum={usage?.connectors?.maximum}
          />
        </div>
      </div>

      {org?.plan === 'grandfather' && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm text-blue-300">
            You're on our legacy plan with unlimited usage. Thanks for being an
            early Connex customer.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Usage Card ──────────────────────────────────────────────────────────

function UsageCard({ icon: Icon, label, current, maximum }) {
  const hasLimit = maximum !== null && maximum !== undefined;
  const pct = hasLimit && maximum > 0 ? Math.min(100, (current / maximum) * 100) : 0;
  const barColor =
    pct >= 100 ? 'bg-red-500' :
    pct >= 80  ? 'bg-yellow-500' :
                 'bg-green-500';

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[#a0a0a0]">
            <Icon className="h-4 w-4" />
            <span className="text-sm">{label}</span>
          </div>
        </div>
        <div className="text-2xl font-semibold text-white">
          {current ?? 0}
          {hasLimit && (
            <span className="text-sm text-[#a0a0a0] font-normal"> of {maximum}</span>
          )}
          {!hasLimit && (
            <span className="text-sm text-[#a0a0a0] font-normal"> used</span>
          )}
        </div>
        {hasLimit && (
          <div className="mt-3 h-1.5 w-full bg-[#2a2a2a] rounded-full overflow-hidden">
            <div
              className={`h-full ${barColor} transition-all`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        {!hasLimit && (
          <p className="text-xs text-[#a0a0a0] mt-2">Unlimited</p>
        )}
      </CardContent>
    </Card>
  );
}